import { BannerForm, IBannerFormData } from '../components/BannerForm'
import { Box, CircularProgress, Typography } from '@material-ui/core'
import React, { useCallback, useState } from 'react'
import { Spacer, useToasts } from '@vestaboard/installables'
import { useHistory, useParams } from 'react-router'

import { Alert } from '@material-ui/lab'
import { AlertDialog } from '../../alert-dialog'
import { ImageUpload } from '../components/ImageUpload'
import { useDeleteHomeScreenBanner } from '../hooks/useDeleteHomescreenBanner'
import { useGetHomeScreenBanner } from '../hooks/useGetHomescreenBanner'
import { useUpdateHomeScreenBanner } from '../hooks/useUpdateHomescreenBanner'

export const UpdateHomeScreenBanner = () => {
  const history = useHistory()
  const { id } = useParams<{ id: string }>()
  const { data, loading, error, refetch } = useGetHomeScreenBanner({ id })
  const [updateBanner] = useUpdateHomeScreenBanner()
  const [deleteBanner] = useDeleteHomeScreenBanner()
  const { addToast } = useToasts()
  const [saving, setSaving] = useState(false)
  const [deleteDialog, setDeleteDialog] = useState(false)

  const banner = data?.homeScreenBanner?.__typename === 'HomeScreenBannerV2' ? data.homeScreenBanner : null

  const handleUpdate = useCallback(
    async (formData: IBannerFormData) => {
      setSaving(true)
      try {
        const result = await updateBanner({
          variables: {
            input: {
              id,
              enabled: formData.enabled || false,
              title: formData.title,
              description: formData.description,
              backgroundColor: formData.backgroundColor,
              positionInFeed: formData.positionInFeed,
              buttons: formData.buttons.map(button => ({
                title: button.title,
                deepLinkUrl: button.deepLinkUrl
              }))
            }
          }
        })

        const updateResult = result.data?.updateHomeScreenBanner

        if (updateResult?.__typename === 'HomeScreenBannerV2') {
          addToast('Banner updated successfully', {
            autoDismiss: true,
            appearance: 'success'
          })
          refetch()
        } else {
          addToast(`Error: ${updateResult?.error || 'Failed to update banner'}`, {
            autoDismiss: true,
            appearance: 'error'
          })
        }
      } catch (error) {
        addToast('Failed to update banner', {
          autoDismiss: true,
          appearance: 'error'
        })
      } finally {
        setSaving(false)
      }
    },
    [updateBanner, addToast, id, refetch]
  )

  const handleDelete = useCallback(async () => {
    try {
      const result = await deleteBanner({
        variables: { id }
      })

      const deleteResult = result.data?.deleteHomeScreenBanner

      if (deleteResult?.__typename === 'HomeScreenBannerV2') {
        addToast('Banner deleted successfully', {
          autoDismiss: true,
          appearance: 'success'
        })
        history.push('/home-screen-banner')
      } else {
        addToast(`Error: ${deleteResult?.error || 'Failed to delete banner'}`, {
          autoDismiss: true,
          appearance: 'error'
        })
      }
    } catch (error) {
      addToast('Failed to delete banner', {
        autoDismiss: true,
        appearance: 'error'
      })
    }
    setDeleteDialog(false)
  }, [deleteBanner, addToast, id, history])

  const handleCancel = useCallback(() => {
    history.push('/home-screen-banner')
  }, [history])

  const handleImageUploaded = useCallback(() => {
    addToast('Banner image updated successfully', {
      autoDismiss: true,
      appearance: 'success'
    })
    refetch()
  }, [addToast, refetch])

  const handleImageDeleted = useCallback(() => {
    refetch()
  }, [refetch])

  if (loading) {
    return <CircularProgress />
  }

  if (error || !banner) {
    return <Alert severity='error'>Banner not found or there was an error loading it.</Alert>
  }

  const bannerData: IBannerFormData = {
    title: banner.title || '',
    enabled: banner.enabled || false,
    description: banner.description || '',
    backgroundColor: banner.backgroundColor || '',
    positionInFeed: banner.positionInFeed,
    buttons:
      banner.buttons?.map(button => ({
        title: button.title,
        deepLinkUrl: button.deepLinkUrl || ''
      })) || []
  }

  return (
    <Box>
      <Typography variant='h4' gutterBottom>
        Edit Banner
      </Typography>
      <Spacer size='large' />

      <BannerForm
        banner={bannerData}
        onSave={handleUpdate}
        onCancel={handleCancel}
        onDelete={() => setDeleteDialog(true)}
        loading={saving}
      />

      <Spacer size='extraLarge' />

      <ImageUpload
        bannerId={id}
        onImageUploaded={handleImageUploaded}
        currentImageUrl={banner.imageUrl || undefined}
        onImageDeleted={handleImageDeleted}
      />

      <AlertDialog
        open={deleteDialog}
        title='Delete Banner'
        description='Are you sure you want to delete this banner? This action cannot be undone.'
        onCancel={() => setDeleteDialog(false)}
        onConfirm={handleDelete}
      />
    </Box>
  )
}
